import React from 'react';
import LegalDocumentPage from '../components/LegalDocumentPage';

/*
 * /privacy — Privacy Policy.
 *
 * Good-faith draft that maps to what the app actually collects (email,
 * username, optional avatar + bio, user-generated content incl. uploaded
 * photos, votes/likes/follows/comments, an anonymous device id for
 * signed-out voting, and basic technical/log data) and how it is used
 * (run the Service, moderation/safety with human report review, security
 * and anti-abuse, and optional emails like the weekly digest). NOTE:
 * posthog-js IS a bundled dependency but is NOT armed — it initialises
 * only when REACT_APP_POSTHOG_KEY is set, and posthog.js additionally
 * refuses to init until PRIVACY_POLICY_COVERS_ANALYTICS there is flipped,
 * which is the acknowledgement that the "Analytics and advertising"
 * section below has been rewritten to disclose what gets collected.
 * Verified 2026-07-30: no key in the live bundle, so this policy is
 * accurate as published.
 * Kept as an array of { heading, body } strings so LegalDocumentPage
 * renders it with no extra wiring. Owner details filled 2026-07-16: entity
 * "Cordell Jenkins II", contact cordelljenkins1914@gmail.com, effective
 * July 16, 2026.
 *
 * NOTE FOR MAINTAINERS (deliberately NOT user-visible): drafted without
 * counsel; have a qualified attorney review before real scale. That
 * caveat belongs HERE, not as the first sentence of the live page.
 */

const SECTIONS = [
  {
    heading: 'Introduction',
    body:
      `Effective date: July 16, 2026. This Privacy Policy explains what ` +
      `information Cordell ` +
      `Jenkins II ("Matchup," "we," "us," or "our") collects when you use the ` +
      `Matchup website and mobile apps (the "Service"), why we collect it, how we ` +
      `use it, and the choices you have. Matchup is a social voting community ` +
      `where people create and vote on matchups, brackets, tier lists, pools, ` +
      `and communities. By using the Service you agree to this Policy; if you do ` +
      `not agree, please do not use the Service.`,
  },
  {
    heading: 'Information you provide',
    body:
      `When you create an account, we collect your email address and your chosen ` +
      `username. Your profile may also include optional information you add, such ` +
      `as an avatar image and a short bio. As you use the Service, we collect the ` +
      `content you create and upload — your matchups, brackets and This-or-That ` +
      `polls, tier lists, item pools, communities, titles, descriptions, ` +
      `comments, and any photos you upload — along with your votes, likes, ` +
      `follows, mentions, reports, and other interactions. Please remember that ` +
      `content you post publicly, and your public profile, can be seen by other ` +
      `people.`,
  },
  {
    heading: 'Signed-out use and the anonymous device identifier',
    body:
      `You can vote a few times without an account. To make the free-vote limit ` +
      `work (currently three votes) and to reduce abuse, we assign your device a ` +
      `random, anonymous identifier that is not tied to your real-world identity. ` +
      `If you later create an account, we may associate the votes you cast while ` +
      `signed out with your new account so your activity carries over. If you ` +
      `never sign up, this identifier is not linked to account data such as your ` +
      `email.`,
  },
  {
    heading: 'Information collected automatically',
    body:
      `Like most online services, when you use Matchup we automatically receive ` +
      `certain technical information, such as your IP address, general device and ` +
      `browser type, app version, and basic log records about your requests and ` +
      `interactions. We use this limited technical data to operate the Service, ` +
      `keep it secure, detect and prevent fraud and abuse (including vote ` +
      `manipulation), and troubleshoot problems.`,
  },
  {
    heading: 'Analytics and advertising',
    body:
      `Matchup does not currently include any third-party advertising, ` +
      `marketing, or cross-app tracking software, and we do not use advertising ` +
      `cookies or device advertising identifiers to profile you. We do not sell ` +
      `your personal information, and we do not share it with advertisers. Any ` +
      `basic, first-party measurement we perform to understand whether the ` +
      `Service is working is used only to run and improve Matchup. If we add ` +
      `analytics tools in the future, we will update this Policy and, where the ` +
      `law requires it, ask for your consent before we begin.`,
  },
  {
    heading: 'How we use your information',
    body:
      `We use the information described above to: provide and operate the ` +
      `Service, including your account, feeds, votes, results, leaderboards, and ` +
      `communities; enable social features such as comments, likes, follows, ` +
      `mentions, and sharing; keep the community safe by reviewing reports and ` +
      `enforcing our Terms, with human review of reports; protect the Service and ` +
      `our users through security, anti-fraud, and anti-abuse measures; ` +
      `communicate with you about your account and about activity relevant to ` +
      `you, and send optional updates such as a weekly email digest that you can ` +
      `turn off in your notification settings; and comply with legal obligations ` +
      `and enforce our agreements. We do not use your content to build ` +
      `advertising profiles.`,
  },
  {
    heading: 'Legal bases (for users in the EEA and UK)',
    body:
      `If you are in the European Economic Area or the United Kingdom, we rely on ` +
      `the following legal bases to process your information: performance of our ` +
      `contract with you, to provide the Service and the features you request; ` +
      `our legitimate interests, to keep the Service secure, prevent abuse and ` +
      `vote manipulation, understand and improve how the Service works, and ` +
      `communicate about relevant activity, balanced against your rights; your ` +
      `consent, for example for optional communications, which you can withdraw ` +
      `at any time; and compliance with our legal obligations. Where we rely on ` +
      `legitimate interests, you may object as described in "Your choices and ` +
      `rights."`,
  },
  {
    heading: 'How we share information',
    body:
      `We do not sell your personal information, and we do not share it with ` +
      `advertisers. We share information only in these limited ways: with service ` +
      `providers who help us run the Service under obligations to protect it — ` +
      `for example, cloud hosting, S3-compatible object storage for uploaded ` +
      `images, and email-delivery providers for account and digest emails; when ` +
      `content or your public profile is shown to other users because you chose ` +
      `to post or share it; to comply with the law or valid legal requests, or to ` +
      `protect the rights, safety, and security of our users, the public, or ` +
      `Matchup, including to investigate abuse or violations of our Terms; and in ` +
      `connection with a business transfer, such as a merger or acquisition, in ` +
      `which case we will require the recipient to honor this Policy or notify ` +
      `you of any material change.`,
  },
  {
    heading: 'How long we keep your information',
    body:
      `We keep your information for as long as your account is active and as ` +
      `needed to provide the Service. When you delete your account, it is ` +
      `deactivated immediately and scheduled for permanent deletion after 30 ` +
      `days; during that window your posts and votes are anonymized to ` +
      `"[deleted]," and you can cancel by signing back in before the 30 days end. ` +
      `After the window closes, we permanently delete your account and associated ` +
      `personal data, except where we are required or permitted to keep certain ` +
      `information for legal, security, dispute-resolution, or backup purposes, ` +
      `and except for anonymized or aggregated data that no longer identifies ` +
      `you. Routine backups are cycled out on a rolling basis.`,
  },
  {
    heading: 'Your choices and rights',
    body:
      `You can review and update your profile, including your avatar and bio, and ` +
      `change your email in Settings. You can make your profile private using the ` +
      `privacy toggle, choose what activity you receive, and turn optional emails ` +
      `like the weekly digest on or off in your notification settings. You can ` +
      `Block or Mute other users to control your experience, and Report content ` +
      `or accounts for review. You can delete your account at any time as ` +
      `described above. Depending on where you live, you may also have rights to ` +
      `access, correct, delete, or receive a copy of your personal information, ` +
      `to object to or restrict certain processing, and to withdraw consent; to ` +
      `exercise these rights, use the in-app controls or contact us at ` +
      `cordelljenkins1914@gmail.com, and we will respond as required by applicable law. We will not ` +
      `discriminate against you for exercising your rights.`,
  },
  {
    heading: 'California privacy rights',
    body:
      `If you are a California resident, you have the right to know what personal ` +
      `information we collect and how we use and disclose it, to request access ` +
      `to or deletion of your personal information, and to be free from ` +
      `discrimination for exercising these rights. We do not sell your personal ` +
      `information, and we do not share it for cross-context behavioral ` +
      `advertising. The categories we collect are described above (such as ` +
      `identifiers like your email and username, your user content, and limited ` +
      `internet or device activity used for security). To make a request, ` +
      `contact us at cordelljenkins1914@gmail.com; we will verify your request as required ` +
      `before responding.`,
  },
  {
    heading: 'European and UK privacy rights',
    body:
      `If you are in the European Economic Area or the United Kingdom, you have ` +
      `rights to access, correct, delete, and receive a portable copy of your ` +
      `personal information, to object to or restrict certain processing, and to ` +
      `withdraw consent where we rely on it. You also have the right to lodge a ` +
      `complaint with your local data-protection authority. To exercise your ` +
      `rights, use the in-app controls or contact us at cordelljenkins1914@gmail.com.`,
  },
  {
    heading: 'International data transfers',
    body:
      `Matchup may be operated from, and use service providers located in, ` +
      `countries other than the one where you live, including the United States. ` +
      `Those countries may have data-protection laws that differ from yours. ` +
      `Where required, we take steps to ensure your information receives an ` +
      `adequate level of protection when it is transferred, such as using ` +
      `appropriate safeguards for cross-border transfers.`,
  },
  {
    heading: 'Security',
    body:
      `We take reasonable measures to protect your information. Traffic between ` +
      `your device and the Service is encrypted in transit using HTTPS/TLS, ` +
      `uploaded images are stored in access-controlled object storage, and we ` +
      `limit access to personal data to those who need it to operate the Service. ` +
      `No method of transmission or storage is 100% secure, however, so we cannot ` +
      `guarantee absolute security. Please help protect your account by using a ` +
      `strong, unique password and keeping your credentials confidential.`,
  },
  {
    heading: "Children's privacy",
    body:
      `Matchup is not intended for children under 13, and you must be at least 13 ` +
      `to use it (or older where local law sets a higher age, as explained in our ` +
      `Terms). We do not knowingly collect personal information from children ` +
      `under 13. If you believe a child under 13 has provided us personal ` +
      `information, contact us at cordelljenkins1914@gmail.com and we will take steps to delete ` +
      `it.`,
  },
  {
    heading: 'Changes to this Policy',
    body:
      `We may update this Privacy Policy from time to time. When we make material ` +
      `changes, we will update the "Last updated" date and, where appropriate, ` +
      `provide additional notice through the Service. Your continued use of the ` +
      `Service after an update takes effect means you accept the revised Policy.`,
  },
  {
    heading: 'Contact us',
    body:
      `If you have questions about this Policy or want to exercise your privacy ` +
      `rights, contact Cordell Jenkins II at cordelljenkins1914@gmail.com.`,
  },
];

const PrivacyPage = () => (
  <LegalDocumentPage
    title="Privacy Policy"
    lastUpdated="July 16, 2026"
    contactEmail="cordelljenkins1914@gmail.com"
    sections={SECTIONS}
    otherDocPath="/terms"
    otherDocLabel="Terms of Service →"
  />
);

export default PrivacyPage;
